import { memo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DollarSign } from "lucide-react";
import type { Treatment } from "@/types/schema";

interface PriceComparisonProps {
  treatment: Treatment;
}

export const PriceComparison = memo(function PriceComparison({ treatment }: PriceComparisonProps) {
  const savings = (treatment.price_usa_max || 0) - (treatment.price_india_min || 0);
  
  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle as="h2" className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-primary flex-shrink-0" />
          <span className="truncate">Price Comparison</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 sm:px-6">
        <div className="flex flex-col gap-4">
          {/* India Price - Hero */}
          <div className="text-center p-4 sm:p-6 bg-primary/5 rounded-xl border-2 border-primary">
            <p className="text-sm text-muted-foreground mb-2">India Price</p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold text-primary break-words">
              ${treatment.price_india_min?.toLocaleString()} - ${treatment.price_india_max?.toLocaleString()}
            </p>
            {treatment.savings_percentage && (
              <Badge className="mt-2 bg-accent text-accent-foreground">
                Save {treatment.savings_percentage}%
              </Badge>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {/* USA Price */}
            <div className="text-center p-3 sm:p-4 bg-muted rounded-xl">
              <p className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">USA Price</p>
              <p className="text-sm sm:text-lg md:text-xl font-semibold text-secondary line-through opacity-60 break-words">
                ${treatment.price_usa_min?.toLocaleString()} - ${treatment.price_usa_max?.toLocaleString()}
              </p>
            </div>
            
            {/* You Save */}
            <div className="text-center p-3 sm:p-4 bg-accent/10 rounded-xl">
              <p className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">You Save</p>
              <p className="text-sm sm:text-lg md:text-xl font-bold text-accent break-words">
                Up to ${savings.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
